/**
 * Host Bridge Abstraction
 *
 * Provides an abstraction layer for communication between the Angular app
 * and its host environment (e.g., ServiceNow portal, standalone browser).
 *
 * Uses the Strategy Pattern to allow different implementations for different hosts.
 */

/**
 * Generic message structure for iframe-host communication
 */
export interface HostMessage<T = any> {
  type: string;
  payload: T;
  requestId?: string;
  timestamp: number;
}

/**
 * User context received from the host environment
 */
export interface UserContext {
  sys_id: string;
  user_name: string;
  display_name: string;
  title?: string;
  email: string;
  avatar_url?: string;
  roles: string[];
  isImpersonating?: boolean;
  impersonatedBy?: {
    sys_id: string;
    user_name: string;
    display_name: string;
    title?: string;
    avatar_url?: string;
  };
}

/**
 * Request payload for ACL permission checks
 */
export interface AclCheckRequest {
  table: string;
  operation: 'read' | 'write' | 'create' | 'delete';
  sys_id?: string;
}

/**
 * Response payload for ACL permission checks
 */
export interface AclCheckResponse {
  allowed: boolean;
  reason?: string;
}

/**
 * Abstract base class for host bridge implementations.
 * Concrete implementations handle communication with specific host environments.
 */
export abstract class HostBridge {
  /**
   * Check if the app is running in embedded (iframe) mode
   */
  abstract isEmbedded(): boolean;

  /**
   * Retrieve the current authenticated user context from the host
   * @throws Error if unable to retrieve user context
   */
  abstract getUserContext(): Promise<UserContext>;

  /**
   * Check ACL permissions against the host's authorization system
   * @param request The ACL check request containing table, operation, and optional record sys_id
   */
  abstract checkAcl(request: AclCheckRequest): Promise<AclCheckResponse>;

  /**
   * Send a one-way message to the host (no response expected)
   * @param type Message type identifier
   * @param payload Message data
   */
  abstract sendMessage(type: string, payload: any): void;

  /**
   * Register a handler for incoming messages from the host
   * @param type Message type to listen for
   * @param handler Callback function to handle the message payload
   */
  abstract onMessage<T>(type: string, handler: (payload: T) => void): void;

  /**
   * Remove a previously registered message handler
   * @param type Message type to stop listening for
   * @param handler The handler function to remove
   */
  abstract offMessage<T>(type: string, handler: (payload: T) => void): void;

  /**
   * Clean up resources (event listeners, pending requests, etc.)
   */
  abstract destroy(): void;
}

/**
 * Message types sent from Angular to host
 */
export const OutgoingMessageTypes = {
  GET_USER_CONTEXT: 'GET_USER_CONTEXT',
  CHECK_ACL: 'CHECK_ACL',
  NAVIGATION_CHANGED: 'NAVIGATION_CHANGED',
  APP_READY: 'APP_READY',
  ERROR: 'ERROR',
} as const;

/**
 * Message types received from host
 */
export const IncomingMessageTypes = {
  USER_CONTEXT_CHANGED: 'USER_CONTEXT_CHANGED',
  REFRESH_DATA: 'REFRESH_DATA',
  NAVIGATE_TO: 'NAVIGATE_TO',
} as const;

export type OutgoingMessageType = (typeof OutgoingMessageTypes)[keyof typeof OutgoingMessageTypes];
export type IncomingMessageType = (typeof IncomingMessageTypes)[keyof typeof IncomingMessageTypes];
